import { Helmet } from 'react-helmet-async'
import { useState } from 'react'
import PageLayout from '../components/layout/PageLayout.jsx'
import SectionHeading from '../components/ui/SectionHeading.jsx'
import { Facebook, Instagram, Linkedin, Mail, Phone, X } from 'lucide-react'
import { motion } from 'framer-motion'

const CONTACT_EMAILS = ['contact@ritikmedicare.in', 'ritikmedicare@gmail.com']
const CONTACT_PHONE = '+91 7996337555'

const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/share/1BaQePuqRu/',
  instagram: 'https://www.instagram.com/brmandal03?igsh=MWM3NmJienZwcW1yYw==',
  linkedin: 'https://www.linkedin.com/in/bikash-ranjan-mandal-13398b23?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app',
  x: 'https://x.com/brmandal03?t=nYoL7eIiBcKTFnMsjsLO4w&s=08',
  threads: 'https://www.threads.com/@brmandal03',
}
const SOCIAL_LINKSCompany = {
  facebook: 'https://www.facebook.com/share/1EwGRcHG3A/',
  instagram: 'https://www.instagram.com/ritikmedicare?igsh=Ym1iazg3NXpqejZ5',
  linkedin: 'https://www.linkedin.com/in/ritik-medicare-b956111b9/', 
}


export default function Contact() {
  const [status, setStatus] = useState({
    loading: false,
    success: null,
    error: null,
  })

  function handleSubmit(e) {
    const formEl = e.currentTarget

    setStatus({ loading: false, success: null, error: null })

    setTimeout(() => {
      setStatus({
        loading: false,
        success: 'Enquiry sent successfully. We will contact you shortly.',
        error: null,
      })

      formEl.reset()
    }, 300)
  }

  return (
    <PageLayout>
      <Helmet>
        <title>Contact Us | Ritik Medicare Pvt. Ltd.</title>
        <meta
          name="description"
          content="Contact Ritik Medicare Pvt. Ltd. for API manufacturing, process development, custom synthesis, contract manufacturing and regulatory support."
        />
        <meta property="og:title" content="Contact Ritik Medicare" />
      </Helmet>

      <section className="bg-sectionBg">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:py-16">
          <SectionHeading
            eyebrow="Contact Us"
            title="Send an enquiry"
            subtitle="We respond quickly for API manufacturing & pharmaceutical solution requirements." 
          />

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="rounded-3xl border border-slate-200 bg-white/70 backdrop-blur p-6"
              >
                <div className="font-extrabold text-slate-900 text-lg">Company Details</div>

                <div className="mt-5 space-y-6">
                  <div className="space-y-2">
                    <div className="font-semibold text-slate-900 text-sm">Social Media</div>
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={SOCIAL_LINKSCompany.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white/70 text-slate-700 hover:text-brand-primary hover:border-brand-primary transition"
                      >
                        <Facebook size={18} />
                      </a>

                      <a
                        href={SOCIAL_LINKSCompany.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white/70 text-slate-700 hover:text-brand-primary hover:border-brand-primary transition"
                      >
                        <Instagram size={18} />
                      </a>

                      <a
                        href={SOCIAL_LINKSCompany.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LinkedIn"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white/70 text-slate-700 hover:text-brand-primary hover:border-brand-primary transition"
                      >
                        <Linkedin size={18} />
                      </a>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-2">
                      <div className="font-semibold text-slate-900 text-sm">Email</div>
                      {CONTACT_EMAILS.map((email) => (
                        <a
                          key={email}
                          href={`mailto:${email}`}
                          className="flex items-center gap-2 text-sm text-slate-700 hover:text-brand-primary"
                        >
                          <Mail size={16} />
                          <span>{email}</span>
                        </a>
                      ))}
                    </div>

                    <a
                      href={`tel:${CONTACT_PHONE}`}
                      className="flex items-center gap-2 text-sm text-slate-700 hover:text-brand-primary"
                    >
                      <Phone size={16} />
                      <span>{CONTACT_PHONE}</span>
                    </a>

                    <div className="text-sm text-slate-600">
                      Kamakunj, Pali Hill No. 3, Near Water Tank 1-G, Tithal Road, Bhagadawada,
                      Taluka - Valsad, Valsad, Gujarat, India - 396001
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="lg:col-span-7">
              <motion.form
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                onSubmit={handleSubmit}
                action="https://formsubmit.co/contact@ritikmedicare.in"
                method="POST"
                className="rounded-3xl border border-slate-200 bg-white p-6"
              >
                <input type="hidden" name="_captcha" value="false" />
                <input
                  type="hidden"
                  name="_subject"
                  value="New Enquiry from Ritik Medicare Website"
                />
                <input type="hidden" name="_template" value="table" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-900">Name</label>
                    <input
                      name="name"
                      required
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-900">Email</label>
                    <input
                      name="email"
                      type="email"
                      required
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-900">Company</label>
                    <input
                      name="company"
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                      placeholder="Company name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-900">Subject</label>
                    <input
                      name="subject"
                      required
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                      placeholder="API manufacturing enquiry"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="text-sm font-semibold text-slate-900">Message</label>
                  <textarea
                    name="message"
                    required
                    rows={6}
                    className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                    placeholder="Tell us about your requirement, timeline and specs…"
                  />
                </div>

                <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <button
                    type="submit"
                    disabled={status.loading}
                    className={
                      'inline-flex items-center justify-center rounded-2xl px-6 py-3 text-sm font-semibold transition ' +
                      (status.loading
                        ? 'bg-brand-primary/40 text-white cursor-not-allowed'
                        : 'bg-brand-primary text-white hover:brightness-110')
                    }
                  >
                    {status.loading ? 'Sending…' : 'Send Enquiry'}
                  </button>

                  <div className="text-sm">
                    {status.success ? (
                      <div className="text-brand-accent font-semibold">{status.success}</div>
                    ) : null}
                    {status.error ? (
                      <div className="text-red-600 font-semibold">{status.error}</div>
                    ) : null}
                  </div>
                </div>
              </motion.form>

              <div className="mt-4 text-xs text-slate-500">
                {/* Keeps original spacing and helper text area, but removes EmailJS-related notes. */}
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  )
}

